import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { Helmet } from "react-helmet-async";
import { fetchAp2025EnggColleges, fetchAp2025PharmacyColleges, updateFavoriteList } from "../api/getApi";
import MobileFilterDrawer from "../components/mobileFilterDrawer";

const UpdateAP2025Lists = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Receive the entire favorite object and index from location state
  const { favorite, index } = location.state || {};

  // --- State Management ---
  const [allColleges, setAllColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  // Initialize state from the passed 'favorite' object
  const [selectedColleges, setSelectedColleges] = useState(favorite?.colleges || []);
  const [listName, setListName] = useState(favorite?.name || "");
  const [listType, setListType] = useState(null); // 'engg' or 'phar'

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filtering State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);

  // UI State
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [districtSearchTerm, setDistrictSearchTerm] = useState("");
  const [isPlaceDrawerOpen, setIsPlaceDrawerOpen] = useState(false);
  const [isBranchDrawerOpen, setIsBranchDrawerOpen] = useState(false);
  const [isDistrictDrawerOpen, setIsDistrictDrawerOpen] = useState(false);

  // --- Effects ---

  useEffect(() => {
    // Redirect to login if user is not authenticated
    if (!isAuthenticated) {
      navigate("/login", { state: { message: "You must be logged in to update a list." } });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Redirect if the favorite object is missing
    if (!favorite) {
      navigate("/profile");
    }
  }, [favorite, navigate]);

  // Determine list type and fetch appropriate colleges
  useEffect(() => {
    const determineListTypeAndLoad = async () => {
      if (!favorite) return;

      try {
        setLoading(true);
        let type = 'engg'; // Default to engineering
        let data;

        // If there are colleges in the favorite list, determine the type
        if (favorite.colleges && favorite.colleges.length > 0) {
          // Expanded detection for pharmacy lists
          const pharmacyKeywords = ['PHM', 'PHD', 'PHARM', 'PHARMACY', 'B.PHARMACY', 'PHARMACEUTIC', 'PHARMACEUT'];
          
          // Check if *all* colleges in the list are pharmacy-related
          const isPharmacy = favorite.colleges.every(college => {
            if (!college.branch_code) return false;
            const branchUpper = college.branch_code.toUpperCase();
            return pharmacyKeywords.some(keyword => branchUpper.includes(keyword));
          });

          type = isPharmacy ? 'phar' : 'engg';
        } else {
          // If the favorite list is empty, check the list name for clues
          const listNameUpper = favorite.name?.toUpperCase() || '';
          const pharmacyNameKeywords = ['PHARMACY', 'PHM', 'PHD', 'PHARM'];
          const isPharmacyByName = pharmacyNameKeywords.some(keyword => listNameUpper.includes(keyword));
          type = isPharmacyByName ? 'phar' : 'engg';
        }

        setListType(type);

        console.log('List type detection:', { 
          type, 
          hasColleges: favorite.colleges.length > 0,
          listName: favorite.name
        });

        // Fetch the appropriate college data based on the determined type
        if (type === 'phar') {
          console.log('Loading pharmacy colleges');
          data = await fetchAp2025PharmacyColleges();
        } else {
          console.log('Loading engineering colleges');
          data = await fetchAp2025EnggColleges();
        }

        console.log(`Loaded ${data.length} colleges for ${type} list`);
        setAllColleges(data);
      } catch (err) {
        setError("Failed to load college data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    determineListTypeAndLoad();
  }, [favorite]);

  // Apply filters
  useEffect(() => {
    if (allColleges.length === 0) return;

    const filtered = allColleges.filter(college => {
      const searchTermLower = searchTerm.toLowerCase();
      const meetsSearch = searchTerm ? (
        college.inst_name?.toLowerCase().includes(searchTermLower) || 
        college.inst_code?.toLowerCase().includes(searchTermLower) || 
        college.place?.toLowerCase().includes(searchTermLower) || 
        college.branch_code?.toLowerCase().includes(searchTermLower)
      ) : true;
      const meetsPlace = selectedPlaces.length > 0 ? selectedPlaces.includes(college.place) : true;
      const meetsBranch = selectedBranches.length > 0 ? selectedBranches.includes(college.branch_code) : true;
      const meetsDistrict = selectedDistricts.length > 0 ? selectedDistricts.includes(college.dist) : true;
      return meetsSearch && meetsPlace && meetsBranch && meetsDistrict;
    });

    setFilteredColleges(filtered);
  }, [allColleges, searchTerm, selectedPlaces, selectedBranches, selectedDistricts]);

  // --- Handlers ---

  const handleBack = () => navigate("/profile");

  const handleUpdateList = async () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { message: "Please log in to update your list." } });
      return;
    }
    if (selectedColleges.length === 0) {
      alert("Your list cannot be empty.");
      return;
    }

    // Construct the full updated favorite object
    const updatedFavorite = {
      ...favorite, // Preserve original fields like shareCode, etc.
      name: listName,
      colleges: selectedColleges,
      timestamp: new Date().toISOString(), // Update the timestamp
    };

    try {
      // The API accepts the list's index and the full updated object
      const data = await updateFavoriteList(index, updatedFavorite);
      if (data.success) {
        alert("List updated successfully!");
        navigate("/profile");
      } else {
        setError(data.message || "Failed to update the list. Please try again.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving.");
      console.error(err);
    }
  };

  const handlePlaceFilterChange = (place) => setSelectedPlaces(prev => prev.includes(place) ? prev.filter(p => p !== place) : [...prev, place]);
  const handleBranchFilterChange = (branch) => setSelectedBranches(prev => prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]);
  const handleDistrictFilterChange = (district) => setSelectedDistricts(prev => prev.includes(district) ? prev.filter(d => d !== district) : [...prev, district]);

  const addCollege = (college) => {
    // Ensure the college matches the list type
    const pharmacyKeywords = ['PHM', 'PHD', 'PHARM', 'PHARMACY', 'B.PHARMACY', 'PHARMACEUTIC', 'PHARMACEUT'];
    const isPharmacyCollege = college.branch_code && pharmacyKeywords.some(keyword => college.branch_code.toUpperCase().includes(keyword));
    const isValidCollege = (listType === 'phar' && isPharmacyCollege) || (listType === 'engg' && !isPharmacyCollege);

    if (!isValidCollege) {
      alert(`Cannot add this college. It does not match the list type (${listType === 'phar' ? 'Pharmacy' : 'Engineering'}).`);
      return;
    }

    if (!selectedColleges.find(c => c._id === college._id)) {
      setSelectedColleges(prev => [...prev, college]);
    }
  };

  const removeCollege = (collegeToRemove) => setSelectedColleges(prev => prev.filter(c => c._id !== collegeToRemove._id));
  const moveCollegeUp = (index) => {
    if (index > 0) {
      const newSelected = [...selectedColleges];
      [newSelected[index - 1], newSelected[index]] = [newSelected[index], newSelected[index - 1]];
      setSelectedColleges(newSelected);
    }
  };
  const moveCollegeDown = (index) => {
    if (index < selectedColleges.length - 1) {
      const newSelected = [...selectedColleges];
      [newSelected[index + 1], newSelected[index]] = [newSelected[index], newSelected[index + 1]];
      setSelectedColleges(newSelected);
    }
  };

  // --- Memoized Values ---
  const uniquePlaces = useMemo(() => [...new Set(allColleges.map(c => c.place))].sort(), [allColleges]);
  const uniqueBranches = useMemo(() => [...new Set(allColleges.map(c => c.branch_code))].sort(), [allColleges]);
  const uniqueDistricts = useMemo(() => [...new Set(allColleges.map(c => c.dist).filter(Boolean))].sort(), [allColleges]);
  const filteredPlaces = uniquePlaces.filter(place => place?.toLowerCase().includes(placeSearchTerm.toLowerCase()));
  const filteredBranches = uniqueBranches.filter(branch => branch?.toLowerCase().includes(branchSearchTerm.toLowerCase()));
  const filteredDistricts = uniqueDistricts.filter(district => district?.toLowerCase().includes(districtSearchTerm.toLowerCase()));

  // --- Render ---
  if (loading && allColleges.length === 0) return <div className="flex justify-center items-center h-screen"><div>Loading...</div></div>;
  if (error) return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <Helmet>
        <title>Update AP 2025 List | CollegeRank</title>
        <meta name="description" content="Update your personalized list of AP 2025 colleges." />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-3">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Update Your {listType === 'phar' ? 'Pharmacy' : 'Engineering'} List</h1>
          <input
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter list name"
          />
        </div>

        <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200 mb-3">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by college, branch, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={() => setIsPlaceDrawerOpen(true)}
              className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Places</span>
                  <span className="block text-sm text-gray-600">{selectedPlaces.length > 0 ? `${selectedPlaces.length} selected` : 'All'}</span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
            <button
              onClick={() => setIsBranchDrawerOpen(true)}
              className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Branches</span>
                  <span className="block text-sm text-gray-600">{selectedBranches.length > 0 ? `${selectedBranches.length} selected` : 'All'}</span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
            <button
              onClick={() => setIsDistrictDrawerOpen(true)}
              className="w-full text-left bg-gray-100 p-2 rounded-lg hover:bg-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">Districts</span>
                  <span className="block text-sm text-gray-600">{selectedDistricts.length > 0 ? `${selectedDistricts.length} selected` : 'All'}</span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white py-4 px-3 rounded-xl shadow-lg border border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4 px-1">Available {listType === 'phar' ? 'Pharmacy' : 'Engineering'} Colleges ({filteredColleges.length})</h2>
            <div className="overflow-auto h-[600px] border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[30%]">College</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[25%]">Branch</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">District</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Fee</th>
                    <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Seats</th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%] sticky right-0 bg-gray-50">Add</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredColleges.map(college => (
                    <tr key={college._id} className="hover:bg-gray-50 relative z-10">
                      <td className="px-2 sm:px-4 py-3 w-[30%]">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          <span>{college.inst_code} - </span>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-900 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis' }}>
                          {college.inst_name}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[25%]">
                        <div className="truncate">{college.branch_code}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[10%]">{college.dist}</td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[15%]">{college.college_fee ? `₹ ${college.college_fee.toLocaleString('en-IN')}` : 'N/A'}</td>
                      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm w-[10%]">{college.convener_seats ?? 'N/A'}</td>
                      <td className="px-2 py-3 text-center w-[10%] sticky right-0 bg-white hover:bg-gray-50">
                        <button
                          onClick={() => addCollege(college)}
                          disabled={selectedColleges.some(c => c._id === college._id)}
                          className="bg-green-500 text-white text-sm sm:text-base font-bold py-1 px-2 sm:px-3 rounded-full hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-transform transform hover:scale-110 relative z-30"
                        >
                          +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Your {listType === 'phar' ? 'Pharmacy' : 'Engineering'} List ({selectedColleges.length})</h2>
            <div className="overflow-auto h-[600px] border rounded-lg">
              {selectedColleges.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {selectedColleges.map((college, index) => (
                    <li key={college._id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-medium text-gray-900 truncate">{index + 1}. {college.inst_name}</p>
                        <p className="text-xs text-gray-500">{college.branch_code} - {college.place}</p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => moveCollegeUp(index)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-1"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => moveCollegeDown(index)}
                          disabled={index === selectedColleges.length - 1}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-30 p-1"
                        >
                          ▼
                        </button>
                        <button
                          onClick={() => removeCollege(college)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          ✖
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 p-4 text-center">
                  Add {listType === 'phar' ? 'pharmacy' : 'engineering'} colleges from the left to build your list.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={handleBack}
            className="w-full bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 transition"
          >
            Back to Profile
          </button>
          <button
            onClick={handleUpdateList}
            disabled={selectedColleges.length === 0}
            className="w-full bg-green-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update List
          </button>
        </div>
      </div>

      <MobileFilterDrawer
        isOpen={isPlaceDrawerOpen}
        onClose={() => setIsPlaceDrawerOpen(false)}
        title="Select Places"
        searchTerm={placeSearchTerm}
        onSearchChange={(e) => setPlaceSearchTerm(e.target.value)}
        filterItems={filteredPlaces}
        selectedItems={selectedPlaces}
        onItemToggle={handlePlaceFilterChange}
        searchPlaceholder="Search places..."
      />
      <MobileFilterDrawer
        isOpen={isBranchDrawerOpen}
        onClose={() => setIsBranchDrawerOpen(false)}
        title="Select Branches"
        searchTerm={branchSearchTerm}
        onSearchChange={(e) => setBranchSearchTerm(e.target.value)}
        filterItems={filteredBranches}
        selectedItems={selectedBranches}
        onItemToggle={handleBranchFilterChange}
        searchPlaceholder="Search branches..."
      />
      <MobileFilterDrawer
        isOpen={isDistrictDrawerOpen}
        onClose={() => setIsDistrictDrawerOpen(false)}
        title="Select Districts"
        searchTerm={districtSearchTerm}
        onSearchChange={(e) => setDistrictSearchTerm(e.target.value)}
        filterItems={filteredDistricts}
        selectedItems={selectedDistricts}
        onItemToggle={handleDistrictFilterChange}
        searchPlaceholder="Search districts..."
      />
    </div>
  );
};

export default UpdateAP2025Lists;